import { useEffect, useId, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
} from '@ludus/ui'
import { LIFECYCLE_PHASES, type LifecyclePhase, type PipelineId } from '@ludus/core'
import { createStatus, PipelineApiError, type CreateStatusInput } from '@/lib/api/pipelines.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'

interface NewStageDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipelineId: PipelineId
  onCreated: () => Promise<unknown>
}

const newStageSchema = z.object({
  name: z.string().trim().min(1, 'required'),
  lifecyclePhase: z.enum(LIFECYCLE_PHASES as unknown as [LifecyclePhase, ...LifecyclePhase[]]),
})

type NewStageFormValues = z.infer<typeof newStageSchema>

export function NewStageDialog({ open, onOpenChange, pipelineId, onCreated }: NewStageDialogProps) {
  const { t } = useTranslation()
  const formId = useId()

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewStageFormValues>({
    resolver: zodResolver(newStageSchema),
    defaultValues: { name: '', lifecyclePhase: 'intake' },
  })

  const [submitError, setSubmitError] = useState<PipelineApiError | null>(null)

  const { trigger, isMutating } = useSWRMutation(
    `/pipelines/${pipelineId}/statuses:create`,
    (_key: string, { arg }: { arg: CreateStatusInput }) => createStatus(pipelineId, arg),
  )

  useEffect(() => {
    if (open) {
      reset({ name: '', lifecyclePhase: 'intake' })
      setSubmitError(null)
    }
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    // The submit button is disabled while mutating; the Enter key is not.
    if (isMutating) return
    setSubmitError(null)
    try {
      await trigger({ name: values.name, lifecyclePhase: values.lifecyclePhase })
      await onCreated()
      onOpenChange(false)
    } catch (err) {
      setSubmitError(
        err instanceof PipelineApiError
          ? err
          : new PipelineApiError({ kind: 'unknown', status: null, message: 'submit failed' }),
      )
    }
  })

  const phaseOptions = LIFECYCLE_PHASES.map((phase) => ({
    value: phase,
    label: t(`pages.pipelines.settings.phase.${phase}`, { defaultValue: phase }),
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.pipelines.settings.addStage.title', { defaultValue: 'New stage' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.pipelines.settings.addStage.description', {
                defaultValue:
                  'The stage joins the end of the board. Its engine role starts inert and is set on the stage card.',
              })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-5">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-5">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.settings.addStage.name', { defaultValue: 'Name' })}
              </span>
              <Input
                {...register('name')}
                autoFocus
                placeholder={t('pages.pipelines.settings.addStage.namePlaceholder', {
                  defaultValue: 'e.g. Qualified',
                })}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <span role="alert" className="text-[11px] font-medium text-toast-error-text">
                  {t('pages.pipelines.settings.addStage.nameRequired', {
                    defaultValue: 'Enter a name.',
                  })}
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.settings.addStage.phase', { defaultValue: 'Lifecycle phase' })}
              </span>
              <Controller
                name="lifecyclePhase"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    options={phaseOptions}
                    onValueChange={(v) => field.onChange(v)}
                    className="w-full"
                    triggerClassName="w-full"
                  />
                )}
              />
            </label>
            {submitError && (
              <p role="alert" className="text-[11px] font-medium text-toast-error-text">
                {apiErrorMessage(submitError, t, 'pages.pipelines.settings.addStage')}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('pages.pipelines.settings.addStage.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm" disabled={isMutating}>
            {t('pages.pipelines.settings.addStage.create', { defaultValue: 'Add stage' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
